// Referee Profile — types + shaping (Phase 3, stat-bar type).
//
// Five bars for one referee (YC, RC, Fouls, Pens, Fouls-per-Card), each scaled
// to the league-wide max across all referees so refs are comparable. Per-game or
// Total, chosen by the rate control. Pure + framework-free; the CSS-animated
// `.stat-bar-fill` width is set inline by the component (see the animate rule).

/** One referee's figures — per-game (yc/rc/…) and season totals (…T). */
export interface RefereeProfileEntry {
  games: number;
  yc: number;
  rc: number;
  fouls: number;
  pens: number;
  ycT: number;
  rcT: number;
  foulsT: number;
  pensT: number;
}

/** Referee Profile data keyed by referee name. */
export type RefereeProfileData = Record<string, RefereeProfileEntry>;

/** One shaped bar: its stat key, width % (2dp string, as legacy), value text. */
export interface RefereeProfileRow {
  key: "yc" | "rc" | "fouls" | "pens" | "fpc";
  pct: string;
  text: string;
}

export interface RefereeProfileView {
  ref: string;
  games: number;
  rows: RefereeProfileRow[];
}

/** Bar width as the legacy renderer computes it: clamped to 100, 2 decimals. */
function barPct(val: number, max: number): string {
  return Math.min(100, (val / (max || 1)) * 100).toFixed(2);
}

/**
 * Shape one referee's five bars for the rate. `max`es are league-wide (across
 * every referee in `data`) so bars are comparable. Returns null if the ref is
 * missing. `rate === "Total"` prints integers verbatim; per-game uses decimals.
 */
export function shapeRefereeProfile(
  data: RefereeProfileData,
  ref: string,
  rate: string | null | undefined
): RefereeProfileView | null {
  const d = data[ref];
  if (!d) return null;

  const total = rate === "Total";
  const all = Object.values(data);
  const scale = total
    ? {
        yc: Math.max(...all.map((r) => r.ycT)),
        rc: Math.max(...all.map((r) => r.rcT)),
        fouls: Math.max(...all.map((r) => r.foulsT)),
        pens: Math.max(...all.map((r) => r.pensT)),
        fpc: Math.max(...all.map((r) => r.foulsT / (r.ycT + r.rcT))),
      }
    : {
        yc: Math.max(...all.map((r) => r.yc)),
        rc: Math.max(...all.map((r) => r.rc)),
        fouls: Math.max(...all.map((r) => r.fouls)),
        pens: Math.max(...all.map((r) => r.pens)),
        fpc: Math.max(...all.map((r) => r.fouls / (r.yc + r.rc))),
      };

  const vals = total
    ? { yc: d.ycT, rc: d.rcT, fouls: d.foulsT, pens: d.pensT }
    : { yc: d.yc, rc: d.rc, fouls: d.fouls, pens: d.pens };
  const fpc = vals.fouls / (vals.yc + vals.rc);
  const fmt = (v: number, decimals: number) =>
    total ? String(v) : v.toFixed(decimals);

  const rows: RefereeProfileRow[] = [
    { key: "yc", pct: barPct(vals.yc, scale.yc), text: fmt(vals.yc, 1) },
    { key: "rc", pct: barPct(vals.rc, scale.rc), text: fmt(vals.rc, total ? 0 : 2) },
    { key: "fouls", pct: barPct(vals.fouls, scale.fouls), text: fmt(vals.fouls, 1) },
    { key: "pens", pct: barPct(vals.pens, scale.pens), text: fmt(vals.pens, total ? 0 : 2) },
    { key: "fpc", pct: barPct(fpc, scale.fpc), text: fpc.toFixed(1) },
  ];

  return { ref, games: d.games, rows };
}
